import Box from '@mui/material/Box';
import React, { FC } from 'react';
import TwTabs from '../../TwTabs/TwTabs';
import TwTab from '../../TwTab/TwTab';
import useAuth from '../../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../../providers/User/User.model';
import { Trip } from '../../../redux/api/trips/trips.model';
import { Order } from '../../../redux/api/orders/orders.model';
import { EntityType } from '../../../redux/Global.model';

interface OverlayTabsProps {
  entityTypeView?: EntityType;
  displaySegments?: Trip[];
  displayOrders?: Order[];
  orderReadableId?: string | number;
  isFilteredView: boolean;
  currentTab: number;
  onTabChange: (_event: React.SyntheticEvent, newValue: string, clickFromTab: boolean) => void;
}

export const OverlayTabs: FC<OverlayTabsProps> = ({
  displaySegments,
  displayOrders,
  entityTypeView,
  orderReadableId,
  isFilteredView,
  currentTab,
  onTabChange
}) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const customerCanSeeTrips = getTenantSetting('customer-access-trips');

  const handleTabChange = (event: React.SyntheticEvent, newValue: string): void => {
    onTabChange(event, newValue, true);
  };

  const showTabs = (): JSX.Element[] | null | undefined => {
    let items: JSX.Element[] | undefined = [];
    if (userData?.role === USER_ROLE.CUSTOMER_CONTACT && !customerCanSeeTrips) {
      items = [];
    } else if (displaySegments && displaySegments?.length > 0) {
      items = displaySegments.map((segment) => {
        return (
          <TwTab
            key={segment.id}
            id={`${orderReadableId}-${segment.sort_order + 1}`}
            label={
              !enableLtl
                ? `Trip #${orderReadableId}-${segment.sort_order + 1}`
                : `Trip #${segment.readable_id}`
            }
          />
        );
      });
    } else if (displayOrders && displayOrders?.length > 0) {
      items = displayOrders?.map((order) => {
        return (
          <TwTab key={order.id} id={`${order.readable_id}`} label={`Order #${order.readable_id}`} />
        );
      });
    }

    const fullRouteTab =
      entityTypeView === EntityType.ORDER ? (
        <TwTab key={isFilteredView ? displaySegments?.length : 0} label={`Order details`} />
      ) : (
        <TwTab key={isFilteredView ? displayOrders?.length : 0} label={`Trip details`} />
      );
    // Full route tab for filtered view like driver should be last
    items && (isFilteredView ? items.push(fullRouteTab) : items.unshift(fullRouteTab));

    return items;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <TwTabs
          variant="scrollable"
          sx={{
            width: '100%',
            alignSelf: 'baseline',

            '& .MuiTabs-scroller': {
              height: '50px'
            },
            '& .MuiTabs-scroller .MuiTabs-indicator': {
              backgroundColor: '#3A4DE9',
              height: '1px',
              border: '1.3px solid #3A4DE9',
              borderRadius: '10px 10px 0 0'
            },
            '& .Mui-selected': {
              textTransform: 'none',
              color: '#1d212c'
            }
          }}
          value={currentTab}
          onChange={handleTabChange}
          aria-label="TwTabs"
          role="TwTabs"
          scrollButtons
        >
          {showTabs()}
        </TwTabs>
      </Box>
    </Box>
  );
};

export default OverlayTabs;
